import { useCallback, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useNavigate } from 'react-router-dom';
import {
  RiMenuLine,
  RiMoonLine,
  RiSunLine,
  RiFullscreenLine,
  RiFullscreenExitLine,
  RiSettings3Line,
  RiLogoutBoxRLine,
  RiGlobalLine,
} from '@remixicon/react';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore, resolveColorMode } from '@/stores/themeStore';
import { MallSwitcher } from './MallSwitcher';
import { NotificationBell } from './NotificationBell';
import { GlobalSearch } from './GlobalSearch';

interface HeaderProps {
  onOpenThemePanel: () => void;
  /**
   * ÜST BAR İKİ ARAYÜZDE DE AYNIDIR — YALNIZCA ARAÇLARI DEĞİŞİR.
   *
   * Kiracı için AVM seçici (tek AVM'si var), global arama (arayacağı
   * kurumsal veri yok) ve bildirim zili (operasyon bildirimleri personele
   * ait) anlamsızdır. Bunları gizlemek için ikinci bir başlık bileşeni
   * yazmak yerine, aynı bileşene hangi arayüzde olduğunu söylüyoruz:
   * böylece tema, dil, tam ekran ve kullanıcı menüsü tek yerde kalıyor ve
   * birinde yapılan düzeltme ikisinde de geçerli oluyor.
   */
  variant?: 'admin' | 'portal';
  /** Portal modunda AVM seçicinin yerini alan bağlam metni. */
  context?: { title: string; subtitle?: string };
}

export function Header({ onOpenThemePanel, variant = 'admin', context }: HeaderProps) {
  const colorMode = useThemeStore((s) => s.colorMode);
  const toggleColorMode = useThemeStore((s) => s.toggleColorMode);
  const toggleSidebar = useThemeStore((s) => s.toggleSidebar);
  const setMobileNavOpen = useThemeStore((s) => s.setMobileNavOpen);
  const mobileNavOpen = useThemeStore((s) => s.mobileNavOpen);

  const isDark = resolveColorMode(colorMode) === 'dark';

  const handleMenuClick = useCallback(() => {
    // Aynı buton iki iş yapar: masaüstünde menüyü daraltır,
    // mobilde kayan menüyü açar/kapatır.
    if (window.innerWidth < 992) setMobileNavOpen(!mobileNavOpen);
    else toggleSidebar();
  }, [mobileNavOpen, setMobileNavOpen, toggleSidebar]);

  return (
    <header className="app-header">
      <button
        type="button"
        className="header-icon-btn"
        onClick={handleMenuClick}
        aria-label="Menüyü aç/kapat"
      >
        <RiMenuLine size={20} aria-hidden="true" />
      </button>

      {variant === 'admin' ? (
        <>
          <MallSwitcher />
          <GlobalSearch />
        </>
      ) : (
        context && (
          <div className="header-context">
            <span className="header-context__title">{context.title}</span>
            {context.subtitle && (
              <span className="header-context__subtitle">{context.subtitle}</span>
            )}
          </div>
        )
      )}

      <div className="ms-auto d-flex align-items-center gap-1">
        <Dropdown align="end">
          <Dropdown.Toggle
            as="button"
            type="button"
            className="header-icon-btn"
            bsPrefix="header-icon-btn"
            aria-label="Dil seçimi"
          >
            <RiGlobalLine size={19} aria-hidden="true" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item active>Türkçe</Dropdown.Item>
            <Dropdown.Item disabled>English (yakında)</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <button
          type="button"
          className="header-icon-btn"
          onClick={toggleColorMode}
          aria-label={isDark ? 'Açık temaya geç' : 'Koyu temaya geç'}
        >
          {isDark ? <RiSunLine size={19} aria-hidden="true" /> : <RiMoonLine size={19} aria-hidden="true" />}
        </button>

        <FullscreenButton />

        {variant === 'admin' && <NotificationBell />}

        <UserMenu />

        <button
          type="button"
          className="header-icon-btn"
          onClick={onOpenThemePanel}
          aria-label="Tema ayarları"
        >
          <RiSettings3Line size={19} aria-hidden="true" />
        </button>
      </div>
    </header>
  );
}

function FullscreenButton() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggle = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      } else {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      }
    } catch {
      // Tarayıcı tam ekranı reddedebilir (izin, iframe, kullanıcı jesti eksik).
      // Bu kritik bir işlev değil; sessizce geçmek doğru davranış.
    }
  }, []);

  return (
    <button
      type="button"
      className="header-icon-btn d-none d-md-grid"
      onClick={toggle}
      aria-label={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
    >
      {isFullscreen ? (
        <RiFullscreenExitLine size={19} aria-hidden="true" />
      ) : (
        <RiFullscreenLine size={19} aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * KULLANICI MENÜSÜ.
 *
 * Ad, rol ve baş harfler oturumdan gelir — sabit metin değildir.
 * Sabit bırakılmış bir isim, demoda hemen fark edilir ve "bu ekran
 * gerçekten benim verimi mi gösteriyor?" sorusunu doğurur.
 */
function UserMenu() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  if (!user) return null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    // `replace`: geri tuşuyla oturum açık sanılan bir ekrana dönülemez.
    navigate('/giris', { replace: true });
  };

  const roleLabel = user.roleNames?.[0] ?? user.roles[0] ?? 'Kullanıcı';

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        as="button"
        type="button"
        className="user-menu__toggle"
        bsPrefix="user-menu__toggle"
        aria-label="Kullanıcı menüsü"
      >
        <span className="user-menu__avatar avatar bg-primary-transparent text-primary-strong">
          {initials(user.fullName)}
        </span>
        <span className="user-menu__meta text-start">
          <span className="user-menu__name d-block">{user.fullName}</span>
          <span className="user-menu__role d-block">{roleLabel}</span>
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: '15rem' }}>
        <Dropdown.Header className="fs-12">
          {user.fullName}
          <div className="text-muted fs-11 fw-normal">{user.email}</div>
          <div className="text-muted fs-11 fw-normal mt-1">{roleLabel}</div>
        </Dropdown.Header>

        <Dropdown.Divider />

        {/*
          MFA DURUMU MENÜDE GÖRÜNÜR.

          Kullanıcının iki adımlı doğrulamasının kapalı olduğunu görmesi
          için ayarlara girmesi gerekmemeli; para hareketi yetkisi olan
          bir hesapta bu, doğrudan uyarı olarak durmalıdır.
        */}
        <Dropdown.ItemText className="fs-11">
          <span className="d-flex align-items-center justify-content-between">
            <span className="text-muted">İki adımlı doğrulama</span>
            <span className={`badge badge-${user.mfaEnabled ? 'success' : 'warning'}-transparent`}>
              {user.mfaEnabled ? 'Açık' : 'Kapalı'}
            </span>
          </span>
        </Dropdown.ItemText>

        <Dropdown.Divider />

        {/*
          SİSTEM AYARLARI KİRACIYA GÖSTERİLMEZ.

          Bağlantı yönetim arayüzüne gider ve kiracı token'ı o ağaca
          giremez; tıklayan kullanıcı kendi ana sayfasına geri atılır.
          Açılamayacak bir menü öğesi, kullanıcıya bir şeyin bozuk
          olduğunu düşündürür.
        */}
        {user.userType === 'STAFF' && (
          <>
            <Dropdown.Item onClick={() => navigate('/sistem/parametreler')}>
              <RiSettings3Line size={16} className="me-2" aria-hidden="true" />
              Sistem Ayarları
            </Dropdown.Item>

            <Dropdown.Divider />
          </>
        )}

        <Dropdown.Item className="text-danger" onClick={handleLogout} disabled={isLoggingOut}>
          <RiLogoutBoxRLine size={16} className="me-2" aria-hidden="true" />
          {isLoggingOut ? 'Çıkış yapılıyor…' : 'Çıkış Yap'}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/** "Furkan Aslan" → "FA" */
function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase('tr-TR'))
    .join('');
}
